import * as yup from "yup";
import { ObjectSchema } from "yup";
import { QSFileFormValues } from "./@types";
import { FileSizeType } from "./components/FileSizeField";
import { MAX_BYTES_SIZE_NUMBER } from "../consts";
import { convertFileSizeToBytes } from "./utils";
import { getFileNameOptionsMenuItems } from "../menuItems";

const testValueIsInteger = (value: string | undefined) =>
  value === undefined || Number.isInteger(Number(value));

const testValueIsLargerOrEqualThan =
  (min: number) => (value: string | undefined) =>
    value === undefined || Number(value) >= min;

const testValueIsSmallerOrEqualThan =
  (max: number) => (value: string | undefined) =>
    value === undefined || Number(value) <= max;

const testSizeWithTypeIsSmallerThanMaxSafeInteger = (
  value: string | undefined,
  sizeType: FileSizeType | undefined
) => {
  if (value === undefined) return true;
  if (sizeType === undefined) return Number(value) <= MAX_BYTES_SIZE_NUMBER;

  const sizeBytes = convertFileSizeToBytes(Number(value), sizeType);
  return sizeBytes <= MAX_BYTES_SIZE_NUMBER;
};

export const FILE_VALIDATION_SCHEMA: ObjectSchema<QSFileFormValues> = yup
  .object()
  .shape({
    fileNameOption: yup
      .string()
      .oneOf(getFileNameOptionsMenuItems().map(item => item.value)),
    fileName: yup.string(),
    fileType: yup.array().of(
      yup.object().shape({
        label: yup.string().required(),
        value: yup.string().required()
      })
    ),
    fromSizeType: yup.string<FileSizeType>().oneOf(["B", "KB", "MB", "GB"]),
    toSizeType: yup.string<FileSizeType>().oneOf(["B", "KB", "MB", "GB"]),
    fromSizeNumber: yup
      .string()
      .test({
        name: "fromSizeNumber_should_be_larger_than_0",
        message: "From size must be greater or equal to 0!",
        test: testValueIsLargerOrEqualThan(0)
      })
      .test({
        name: "fromSizeNumber_should_be_smaller_than_max_safe_integer",
        message: `From size must be smaller than ${MAX_BYTES_SIZE_NUMBER} bytes!`,
        test(value: string | undefined) {
          const fromSizeType = this.parent.fromSizeType;
          return testSizeWithTypeIsSmallerThanMaxSafeInteger(
            value,
            fromSizeType
          );
        }
      }),
    toSizeNumber: yup
      .string()
      .test({
        name: "toSizeNumber_should_be_larger_than_0",
        message: "To size must be greater or equal to 0!",
        test: testValueIsLargerOrEqualThan(0)
      })
      .test({
        name: "toSizeNumber_should_be_smaller_than_max_safe_integer",
        message: `To size must be smaller than ${MAX_BYTES_SIZE_NUMBER} bytes!`,
        test(value: string | undefined) {
          const toSizeType = this.parent.toSizeType;
          return testSizeWithTypeIsSmallerThanMaxSafeInteger(value, toSizeType);
        }
      })
      .test({
        name: "toSizeNumber_should_be_larger_than_fromSizeNumber",
        message: "To size must be greater than from size!",
        test(value: string | undefined) {
          if (value === undefined) return true;

          const fromSizeNumber = this.parent.fromSizeNumber;
          const fromSizeType = this.parent.fromSizeType;
          const toSizeType = this.parent.toSizeType;

          if (!fromSizeNumber) return true;

          const fromSizeBytes = convertFileSizeToBytes(
            Number(fromSizeNumber),
            fromSizeType as FileSizeType
          );
          const toSizeBytes = convertFileSizeToBytes(
            Number(value),
            toSizeType as FileSizeType
          );

          return toSizeBytes >= fromSizeBytes;
        }
      }),
    antivirusDetectionsCountFrom: yup
      .string()
      .test({
        name: "antivirusDetectionsCountFrom_should_be_larger_than_0",
        message: "From size must be greater or equal to 0!",
        test: testValueIsLargerOrEqualThan(0)
      })
      .test({
        name: "antivirusDetectionsCountFrom_should_be_an_integer",
        message: "From size must be an integer!",
        test: testValueIsInteger
      }),
    antivirusDetectionsCountTo: yup
      .string()
      .test({
        name: "antivirusDetectionsCountTo_should_be_larger_than_0",
        message: "To size must be greater or equal to 0!",
        test: testValueIsLargerOrEqualThan(0)
      })
      .test({
        name: "antivirusDetectionsCountTo_should_be_an_integer",
        message: "To size must be an integer!",
        test: testValueIsInteger
      })
      .test({
        name: "antivirusDetectionsCountTo",
        message: "To size must be greater than from size",
        test(value: string | undefined) {
          if (value === undefined) return true;

          const fromAntivirusDetectionsCount = Number(
            this.parent.antivirusDetectionsCountFrom
          );
          if (fromAntivirusDetectionsCount)
            return fromAntivirusDetectionsCount <= Number(value);

          return true;
        }
      }),
    fileTags: yup.array().of(
      yup.object().shape({
        label: yup.string().required(),
        value: yup.string().required()
      })
    ),
    threatName: yup.string(),
    classification: yup.array().of(
      yup.object().shape({
        label: yup.string().required(),
        value: yup.string().required()
      })
    ),
    threatScore: yup
      .string()
      .test({
        name: "threatScore_should_be_an_integer",
        message: "Threat score must be an integer!",
        test: testValueIsInteger
      })
      .test({
        name: "threatScore_should_be_larger_or_equal_than_1",
        message: "Threat score must be greater or equal to 1!",
        test: testValueIsLargerOrEqualThan(1)
      })
      .test({
        name: "threatScore_should_be_smaller_or_equal_than_10",
        message: "Threat score must be smaller or equal to 10!",
        test: testValueIsSmallerOrEqualThan(10)
      }),
    attackTechnique: yup.array().of(
      yup.object().shape({
        label: yup.string().required(),
        value: yup.string().required()
      })
    ),
    avVendors: yup.array().of(
      yup.object().shape({
        label: yup.string().required(),
        value: yup.string().required()
      })
    ),
    submissionTime: yup
      .object()
      .shape({
        label: yup.string(),
        value: yup.string()
      })
      .test({
        name: "submissionTime_custom_timerange_should_be_valid",
        message: "Submission time custom time range should be valid!",
        test(value: { label: string; value: string }) {
          if (value.label !== "custom") return true;

          const [from, to] = value.value.split(" TO ");
          const fromTime = new Date(from.slice(1)).getTime();
          const toTime = new Date(to.slice(0, -1)).getTime();

          return fromTime <= toTime;
        }
      }),
    staticAnalysisIndicators: yup.array().of(
      yup.object().shape({
        label: yup.string().required(),
        value: yup.string().required()
      })
    )
  });
